import React, { useState } from 'react';

import { ScreenHead } from '../../ui/screen-parts.js';
import { Chip, Field, Kpis, Panel, Segments, Empty, useFlash, readForm } from '../../ui/kit.js';
import { day, printDate, nextId } from '../../state/building.js';

import StaffTeam from '../../media/images/modules/staff-team.jpg';

const TEAMS = ['Front desk', 'Security', 'Valet', 'Maintenance'];
const PRIORITY_TONE = { High: 'alert', Normal: 'info', Low: 'muted' };

const SEED = [
    { id: 1, task: 'Confirm guest suite A check-in for Unit 2104', team: 'Front desk', due: day(0), priority: 'Normal', done: false },
    { id: 2, task: 'Log packages from the 10:00 AM Amazon drop', team: 'Front desk', due: day(0), priority: 'Normal', done: true },
    { id: 3, task: 'Print move-in welcome packet for Unit 604', team: 'Front desk', due: day(1), priority: 'Low', done: false },
    { id: 4, task: 'Test lobby and garage CCTV recording', team: 'Security', due: day(0), priority: 'High', done: false },
    { id: 5, task: 'Walk stairwells A and B, check exit doors latch', team: 'Security', due: day(0), priority: 'Normal', done: true },
    { id: 6, task: 'Update contractor badge list for the 1502 renovation', team: 'Security', due: day(1), priority: 'Normal', done: false },
    { id: 7, task: 'Stage resident vehicles for the 5:00 PM rush', team: 'Valet', due: day(0), priority: 'Normal', done: false },
    { id: 8, task: 'Reconcile key box against the P2 ticket log', team: 'Valet', due: day(0), priority: 'High', done: false },
    { id: 9, task: 'Charge EV stations bay 4 fault report to vendor', team: 'Valet', due: day(2), priority: 'Low', done: true },
    { id: 10, task: 'Replace north sconce lamp in the lobby', team: 'Maintenance', due: day(0), priority: 'Low', done: false },
    { id: 11, task: 'Check pool heater setpoint before the weekend', team: 'Maintenance', due: day(1), priority: 'Normal', done: false },
    { id: 12, task: 'Clear AC condensate line, Unit 803', team: 'Maintenance', due: day(0), priority: 'High', done: true }
];

const meterTone = pct => (pct < 25 ? ' meter--alert' : pct < 50 ? ' meter--warn' : '');

/* Staff Task Management: each team's shift list, checked off as it gets
   done, with completion tracked team by team. */
function TasksScreen() {
    const [tasks, setTasks] = useState(SEED);
    const [team, setTeam] = useState('All');
    const [flash, notify] = useFlash();

    const toggle = item => {
        setTasks(list => list.map(t => (t.id === item.id ? { ...t, done: !t.done } : t)));
        if (!item.done) notify(`${item.team}: ${item.task} checked off.`);
    };

    const submit = event => {
        const values = readForm(event, ['task']);
        if (!values) return notify('Describe the task first.');

        setTasks(list => [{
            id: nextId(),
            task: values.task,
            team: values.team,
            due: printDate(values.due),
            priority: values.priority,
            done: false
        }, ...list]);
        notify(`Task added to the ${values.team} list.`);
    };

    const shown = tasks.filter(t => team === 'All' || t.team === team);
    const open = tasks.filter(t => !t.done);
    const progress = name => {
        const mine = tasks.filter(t => t.team === name);
        return { done: mine.filter(t => t.done).length, total: mine.length };
    };

    return (
        <div className="screen-content">
            <ScreenHead eyebrow="Task management" title="Staff tasks" />

            <div className="banner">
                <img src={StaffTeam} alt="" />
                <p className="banner__caption">Front desk, security, valet and maintenance, one shift list per team.</p>
            </div>

            <Kpis items={[
                { label: 'Open', value: open.length, tone: 'info' },
                { label: 'High priority', value: open.filter(t => t.priority === 'High').length, tone: 'alert' },
                { label: 'Due today', value: open.filter(t => t.due === day(0)).length, tone: 'warn' },
                { label: 'Done', value: tasks.length - open.length, tone: 'ok' }
            ]} />

            {flash}

            <div className="split">
                <div className="stack">
                    <Panel as="form" className="panel--accent" onSubmit={submit}>
                        <h4>New task</h4>
                        <div className="form">
                            <Field label="Task" wide>
                                <input name="task" className="form-control" placeholder="e.g. Reset pool deck umbrellas" />
                            </Field>
                            <Field label="Team">
                                <select name="team" className="form-control" defaultValue="Front desk">
                                    {TEAMS.map(t => <option key={t}>{t}</option>)}
                                </select>
                            </Field>
                            <Field label="Priority">
                                <select name="priority" className="form-control" defaultValue="Normal">
                                    {Object.keys(PRIORITY_TONE).map(p => <option key={p}>{p}</option>)}
                                </select>
                            </Field>
                            <Field label="Due" wide>
                                <input type="date" name="due" className="form-control" />
                            </Field>
                            <button type="submit" className="btn btn-primary">Add task</button>
                        </div>
                    </Panel>

                    <Panel index={1}>
                        <h4>Completion by team</h4>
                        {TEAMS.map(name => {
                            const { done, total } = progress(name);
                            const pct = total ? Math.round((done / total) * 100) : 0;
                            return (
                                <div key={name} className="stack" style={{ gap: '0.35rem' }}>
                                    <div className="row__end" style={{ justifyContent: 'space-between' }}>
                                        <span className="row__title">{name}</span>
                                        <span className="row__meta">{done} of {total} · {pct}%</span>
                                    </div>
                                    <div className={`meter${meterTone(pct)}`}>
                                        <span className="meter__fill" style={{ width: `${pct}%` }} />
                                    </div>
                                </div>
                            );
                        })}
                    </Panel>
                </div>

                <div className="stack">
                    <Segments label="Team" options={['All', ...TEAMS]} value={team} onChange={setTeam} />
                    {shown.length === 0 ? <Empty>No tasks for this team.</Empty> : (
                        <ul className="checks">
                            {shown.map(t => (
                                <li key={t.id}>
                                    <button
                                        type="button"
                                        className={`check${t.done ? ' check--on' : ''}`}
                                        aria-pressed={t.done}
                                        onClick={() => toggle(t)}
                                    >
                                        <span className="check__box">{t.done ? '✓' : ''}</span>
                                        <span className="check__text" style={{ flex: 1 }}>
                                            {t.task}
                                            <span className="row__meta"> · {t.team} · due {t.due}</span>
                                        </span>
                                        <Chip tone={PRIORITY_TONE[t.priority]}>{t.priority}</Chip>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}

export default TasksScreen;
